import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { track } from '../../utils/track';
import useSeoDocumentTitle from '../useSeoDocumentTitle';
import { SeoFooter } from '../components/Panels';
import { CORRECTIONS_CONTACT, THRESHOLDS } from '../siteConfig';
import { GapMark } from '../components/GapChip';

const CONTENTS = [
  ['sources', 'Where the data comes from'],
  ['matching', 'Matching guides to results'],
  ['gap', 'Guide gap'],
  ['error', 'Typical miss'],
  ['samples', 'Minimum samples'],
  ['forecast', 'Forecasts'],
  ['limits', 'What the figures don’t say'],
  ['corrections', 'Corrections and right of reply'],
];

export default function MethodologyPage() {
  useSeoDocumentTitle('methodology', null);
  const { hash } = useLocation();
  useEffect(() => {
    track('methodology_view', {});
  }, []);
  // Client-side navigation to /methodology#anchor doesn't scroll by itself.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <main className="re-seo re-seo--doc" id="main">
      <div className="re-seo__inner re-doc">
        <header className="re-masthead">
          <p className="re-kicker">Methodology</p>
          <h1>How we measure price guides</h1>
          <p className="re-lede">
            Every figure on RealEstats comes from two records of the same home: the price guide it was advertised at, and
            the price it sold for. This page explains how the two are matched and what each number means.
          </p>
        </header>

        <nav className="re-doc__toc" aria-label="On this page">
          <ol>
            {CONTENTS.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`}>{label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="sources">
          <h2>Where the data comes from</h2>
          <p>
            <strong>Price guides</strong> are captured from public listing pages on the major Australian property portals
            while a home is on the market. Each capture is stored with the page it came from, when it was taken, and a
            checksum, so every guide can be traced back to its source.
          </p>
          <p>
            <strong>Sale prices</strong> come from NSW government property sales records and from sold results published
            on the same portals.
          </p>
        </section>

        <section id="matching">
          <h2>Matching guides to results</h2>
          <p>
            A listing is matched to a sale when both refer to the same property and the sale follows the listing. The guide
            used is the one advertised on the matched listing. Listings without a numeric guide (&ldquo;Contact agent&rdquo;,
            &ldquo;Auction&rdquo; with no range) can&rsquo;t be measured and are left out. So are sales we can&rsquo;t
            confidently link to a listing.
          </p>
          <p>
            A <em>sale with a published guide</em> (the count shown next to every figure) is a sale that passed this
            matching.
          </p>
          <p>
            Reports confirmed to describe the same sale are counted once. Records with unresolved property, date,
            price or guide conflicts are left out until the source evidence can be reconciled. The original records
            are retained so these decisions can be reviewed.
          </p>
        </section>

        <section id="gap">
          <h2>Guide gap</h2>
          <p>How far the sale price landed from the advertised guide:</p>
          <p className="re-formula">
            guide gap = (sale price &minus; guide midpoint) &divide; guide midpoint
          </p>
          <p>
            For a range guide such as $1.5M&ndash;$1.65M, the midpoint is $1.575M. A single-figure guide is its own
            midpoint. So a home guided at $1.5M&ndash;$1.65M that sold for $1.81M has a gap of +14.9%.
          </p>
          <p>
            Where the original guide range has not been retained, the results table shows the stored value labelled{' '}
            <strong>Guide midpoint</strong>. The gap uses this midpoint; it does not reconstruct the original range.
          </p>
          <ul className="re-doc__legend">
            <li>
              <GapMark direction="above" /> <strong>Above guide</strong>: sold for more than the guide midpoint.
            </li>
            <li>
              <GapMark direction="below" /> <strong>Below guide</strong>: sold for less.
            </li>
            <li>
              <GapMark direction="at" /> <strong>At guide</strong>: within half a percent either way.
            </li>
          </ul>
          <p>
            Suburb figures use the <strong>median</strong> gap, so one unusual sale can&rsquo;t move them far. Agent figures
            use the <strong>average</strong> gap across their sales.
          </p>
        </section>

        <section id="error">
          <h2>Typical miss</h2>
          <p>
            The guide gap has a direction, so a +10% result and a &minus;10% result would average out to zero. The{' '}
            <strong>typical miss</strong> ignores direction: it is the average size of the gap, whichever way it went.
            A lower typical miss means results landed closer to the guide.
          </p>
          <p>Agent rankings order agents by typical miss, smallest first.</p>
        </section>

        <section id="samples">
          <h2>Minimum samples</h2>
          <p>Small samples produce noisy numbers, so figures only appear once there is enough evidence behind them:</p>
          <ul>
            <li>
              Suburb figures are ranked and listed in search once a suburb has at least{' '}
              {THRESHOLDS.suburbMinGuideMatched12m} sales with a published guide in the last 12 months.
            </li>
            <li>
              Agents are ranked once they have at least {THRESHOLDS.agentMinGuideMatched24m} sales with a published guide in
              the last two years.
            </li>
            <li>
              On monthly charts, months with fewer than {THRESHOLDS.lowSampleMonth} sales are drawn as hollow points.
              They swing more and deserve less weight.
            </li>
          </ul>
          <p>Pages below these thresholds still show their numbers, labelled as limited data.</p>
        </section>

        <section id="forecast">
          <h2>Forecasts</h2>
          <p>
            Where a forecast appears, it describes a <strong>comparable-home index</strong>: the expected change in price
            for a basket of similar homes, weighted by reporting quality. It isn&rsquo;t the change in the median sale
            price, which moves with the mix of homes that happen to sell. And it isn&rsquo;t a valuation of any single
            property. Forecasts are only published for areas where the model has enough history to be tested, and always
            with a range.
          </p>
        </section>

        <section id="limits">
          <h2>What the figures don&rsquo;t say</h2>
          <p>
            A result above guide can come from a guide set low, or from strong competition on the day, or from the market
            moving between listing and sale. These figures measure the outcome. They don&rsquo;t establish why it happened,
            and nothing on this site is a claim about any agent&rsquo;s intent or conduct.
          </p>
        </section>

        <section id="corrections">
          <h2>Corrections and right of reply</h2>
          <p>
            If a sale is attributed to the wrong agent, matched to the wrong listing, or recorded at the wrong price, we
            want to fix it. Corrections are checked against the source records and applied at the next data refresh.
          </p>
          {CORRECTIONS_CONTACT?.email ? (
            <p>
              Email <strong>{CORRECTIONS_CONTACT.email}</strong> with the property address and what needs correcting.
            </p>
          ) : (
            <p>A dedicated corrections contact is being set up and will be listed here.</p>
          )}
          <p>
            Agents will also be able to claim their profile to add context to their figures. <Link to="/leaderboard">See the agent leaderboard</Link>.
          </p>
        </section>

        <SeoFooter />
      </div>
    </main>
  );
}
